import { FormattedMessage, useIntl } from 'react-intl';
import * as Icons from '../../assets/icons';
import s from './Tile.module.scss';

type IconComponentName = keyof typeof Icons;

interface TileProps {
  /** React-intl id of the label text */
  label: string;
  /** Name of the form input this tile is part of */
  name: string;
  /** The value this tile represents */
  value: string;
  /** The name of the icon component to render, see icons/index.js for available icons */
  iconComponent: IconComponentName;
  /** True if this value is currently selected */
  checked: boolean;
  /** When the input is selected this will be called with the tile's value */
  onChange: (value: string) => void | Promise<void>;
}

/**
 * The tile component is a radio button that is styled to look like a tile.
 * It's state needs to be controlled by the parent component via the *checked*
 * and *onChange* props.
 */
function Tile({
  label,
  name,
  value,
  iconComponent,
  checked,
  onChange,
}: TileProps) {
  const intl = useIntl();
  const Icon = Icons[iconComponent];

  return (
    <button
      type="button"
      id={`${name}-${value}`}
      aria-label={
        checked
          ? `${intl.formatMessage({ id: label })}. ${intl.formatMessage({
              id: 'previouslySelected',
            })}`
          : intl.formatMessage({ id: label })
      }
      onClick={() => onChange(value)}
      className={s.button}
    >
      <div className={`${s.tile} ${checked ? s.checked : ''}`}>
        <div className={s.image}>
          <Icon role="presentation" focusable="false" />
        </div>
        <div className={s.labeltext}>
          <FormattedMessage id={label} />
        </div>
      </div>
    </button>
  );
}

export default Tile;
export type { IconComponentName };
